import type { NumberResolver } from '@pct-ts/typing';
import { isNumber } from '@pct-ts/typeguard';

/**
 * Resolves a number value based on a resolver function or a static number.
 *
 * This function accepts either a number directly or a function that returns a number.
 * If a number is provided, it's returned as-is. If a function is provided, it's called
 * with the given parameter to generate the number.
 *
 * @param resolver - A number value or a function that resolves to a number
 * @param param - The parameter to pass to the resolver function
 * @returns The resolved number value
 *
 * @example
 * // Using a static number
 * const static = resolveNumber(42, {});
 * console.log(static); // Output: 42
 *
 * @example
 * // Using a resolver function
 * const dynamic = resolveNumber(
 *   (item: { value: number }) => item.value,
 *   { value: 42 }
 * );
 * console.log(dynamic); // Output: 42
 */
export function resolveNumber<T>(resolver: NumberResolver<T>, param: T): number {
  if (isNumber(resolver)) {
    return resolver;
  }
  return resolver(param);
}
